/**
 * Profile Queries
 *
 * Handles user profile operations including nickname and bio
 */

import { supabase, getCurrentUserId } from '../supabase';

// ============================================================================
// TYPES
// ============================================================================

export interface UserProfile {
  id: string;
  nickname: string | null;
  bio: string | null;
  postal_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateProfileData {
  nickname?: string;
  bio?: string;
  postal_code?: string;
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Get current user's profile
 */
export async function getCurrentUserProfile(): Promise<UserProfile | null> {
  const userId = getCurrentUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await (supabase
    .from('user_profiles') as any)
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    // If profile doesn't exist, create it
    if (error.code === 'PGRST116') {
      return await createUserProfile();
    }
    throw error;
  }

  return data;
}

/**
 * Get profile by user ID
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await (supabase
    .from('user_profiles') as any)
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return null; // Profile doesn't exist
    }
    throw error;
  }

  return data;
}

/**
 * Create user profile
 */
export async function createUserProfile(): Promise<UserProfile> {
  const userId = getCurrentUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await (supabase
    .from('user_profiles') as any)
    .insert({
      id: userId,
      nickname: null,
      bio: null
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update user profile
 */
export async function updateUserProfile(updates: UpdateProfileData): Promise<UserProfile> {
  const userId = getCurrentUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  // Validate nickname if provided
  if (updates.nickname !== undefined) {
    const trimmed = updates.nickname?.trim();
    if (trimmed && (trimmed.length < 3 || trimmed.length > 50)) {
      throw new Error('Το ψευδώνυμο πρέπει να έχει 3-50 χαρακτήρες');
    }
  }

  // Validate bio if provided
  if (updates.bio !== undefined && updates.bio && updates.bio.length > 500) {
    throw new Error('Η βιογραφία δεν μπορεί να υπερβαίνει τους 500 χαρακτήρες');
  }

  const { data, error } = await (supabase
    .from('user_profiles') as any)
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    // Handle unique constraint violation for nickname
    if (error.code === '23505') {
      throw new Error('Αυτό το ψευδώνυμο χρησιμοποιείται ήδη');
    }
    throw error;
  }

  return data;
}

/**
 * Check if nickname is available
 */
export async function checkNicknameAvailability(nickname: string): Promise<boolean> {
  const trimmed = nickname.trim();

  if (trimmed.length < 3 || trimmed.length > 50) {
    return false;
  }

  const { data, error } = await (supabase
    .from('user_profiles') as any)
    .select('id')
    .eq('nickname', trimmed)
    .maybeSingle();

  if (error) {
    console.error('Error checking nickname:', error);
    return false;
  }

  // Nickname is available if no profile found with that nickname
  return data === null;
}

/**
 * Check if current user has completed their profile
 */
export async function hasCompletedProfile(): Promise<boolean> {
  try {
    const profile = await getCurrentUserProfile();
    return profile !== null && profile.nickname !== null && profile.nickname.trim().length > 0;
  } catch (error) {
    console.error('Error checking profile completion:', error);
    return false;
  }
}
